import { useCallback, useEffect, useRef } from 'react'
import type { VoiceAccess } from 'lib/types'
import { refreshDelayMs } from './protocol'

// Shortest gap between two on-demand refetches (socket trouble can fire often).
const MIN_REFRESH_GAP_MS = 30_000

// Keeps a fleet voice token current. The token comes from the org's `voice`
// block on `/users/current` and lives an hour; it is refetched five minutes
// before it expires, and on demand when the socket keeps failing to open.
export function useFleetCredentials(voice: VoiceAccess | null | undefined, refetch: () => Promise<unknown>) {
  const tokenRef = useRef<string | null>(voice?.token ?? null)
  tokenRef.current = voice?.token ?? null

  const refetchRef = useRef(refetch)
  refetchRef.current = refetch
  const lastRefreshRef = useRef(0)

  const runRefetch = useCallback(() => {
    lastRefreshRef.current = Date.now()
    refetchRef.current().catch(err => console.warn('[Fleet] voice token refresh failed', err))
  }, [])

  const refresh = useCallback(() => {
    if (Date.now() - lastRefreshRef.current < MIN_REFRESH_GAP_MS) return
    runRefetch()
  }, [runRefetch])

  const expiresAt = voice?.backend === 'fleet' ? voice.expiresAt : null
  useEffect(() => {
    const delay = refreshDelayMs(expiresAt, Date.now())
    if (delay == null) return
    const timer = setTimeout(runRefetch, delay)
    return () => clearTimeout(timer)
  }, [expiresAt, runRefetch])

  const getToken = useCallback(() => tokenRef.current, [])

  return { getToken, refresh }
}
